import { computed, onBeforeUnmount, readonly, ref, watch } from 'vue';
import type { Ref } from 'vue';

import { useAssignmentHover } from '@/composables/useAssignmentHover';
import { clampToBounds, connectorChains } from '@/utils/scheduleConnectors';
import type { ConnectorBox, ConnectorPoint } from '@/utils/scheduleConnectors';

// Fallback for a card whose colour cannot be read — slate-500, quiet enough to read as a
// connector rather than as a status.
const FALLBACK_COLOR = 'rgb(100, 116, 139)';

export interface ConnectorRoute {
  id: string;
  color: string;
  path: string;
  points: ConnectorPoint[];
}

// Draws the dotted route between the blocks of a PO the reader has singled out — by hovering
// it, by selecting it, or both at once, each in its own customer colour so a hover never
// blanks a standing selection.
//
// Positions come from the live DOM rather than from the board model: a card's geometry is
// decided by the table's column widths, the cell's own scroll position and how many blocks
// share a week, none of which the model knows. Only the pure geometry is testable, and that
// lives in utils/scheduleConnectors.
export function useScheduleConnectors(canvas: Ref<HTMLElement | null>, selectedBlockKey: Ref<string | null | undefined>) {
  const { hoveredBlockKey } = useAssignmentHover();
  const routes = ref<ConnectorRoute[]>([]);

  let frame: number | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let listening = false;

  // Selection first so a standing selection keeps its place in the list as hovers come and go.
  const activeKeys = computed(() => {
    const keys = [selectedBlockKey.value, hoveredBlockKey.value].filter((key): key is string => Boolean(key));
    return [...new Set(keys)];
  });

  function measure(): void {
    const root = canvas.value;
    if (!root) {
      routes.value = [];
      return;
    }
    const origin = root.getBoundingClientRect();
    routes.value = activeKeys.value.flatMap((key) => routesFor(root, origin, key));
  }

  function routesFor(root: HTMLElement, origin: DOMRect, key: string): ConnectorRoute[] {
    const cards = [...root.querySelectorAll<HTMLElement>(`[data-block-key="${CSS.escape(key)}"]`)];
    if (cards.length < 2) return [];

    const laneIds = new Map<Element, string>();
    const boxes = cards.map<ConnectorBox>((card) => boxFor(card, origin, laneIds));
    // The card's own left border already carries the PO's customer colour, so reading it back
    // keeps the line and the cards in step without resolving the colour a second time.
    const color = window.getComputedStyle(cards[0]).borderLeftColor || FALLBACK_COLOR;

    return connectorChains(boxes).map((chain) => ({
      id: `${key}:${chain.laneId}`,
      color,
      path: chain.path,
      points: chain.points,
    }));
  }

  function boxFor(card: HTMLElement, origin: DOMRect, laneIds: Map<Element, string>): ConnectorBox {
    const rect = card.getBoundingClientRect();
    const box = {
      laneId: laneIdFor(card.closest('tr'), laneIds),
      left: rect.left - origin.left,
      right: rect.right - origin.left,
      top: rect.top - origin.top,
      bottom: rect.bottom - origin.top,
    };
    // The cell is the card's visible box: anything the cell's own scroller has pushed out of
    // sight has to be clamped back to it, or the line runs off into the rows below.
    const cell = card.closest('td');
    if (!cell) return box;
    const bounds = cell.getBoundingClientRect();
    return clampToBounds(box, {
      left: bounds.left - origin.left,
      right: bounds.right - origin.left,
      top: bounds.top - origin.top,
      bottom: bounds.bottom - origin.top,
    });
  }

  function schedule(): void {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      measure();
    });
  }

  // Scroll is captured rather than bubbled, because three different scrollers move the cards:
  // the grid viewport as you pan (which also mounts and unmounts week columns, changing which
  // siblings exist), a week cell whose blocks overflow it, and the page itself.
  function startListening(): void {
    if (listening) return;
    listening = true;
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (canvas.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(canvas.value);
    }
  }

  function stopListening(): void {
    if (!listening) return;
    listening = false;
    document.removeEventListener('scroll', schedule, { capture: true });
    window.removeEventListener('resize', schedule);
    resizeObserver?.disconnect();
    resizeObserver = null;
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
  }

  // Listeners exist only while a PO is singled out: measuring is DOM reads on every scroll
  // frame, and the board is scrolled far more often than anything on it is hovered or selected.
  watch(
    activeKeys,
    (keys) => {
      if (keys.length > 0) {
        startListening();
        schedule();
        return;
      }
      stopListening();
      routes.value = [];
    },
    { immediate: true },
  );

  onBeforeUnmount(stopListening);

  return { routes: readonly(routes) };
}

function laneIdFor(row: Element | null, laneIds: Map<Element, string>): string {
  if (!row) return 'orphan';
  const existing = laneIds.get(row);
  if (existing) return existing;
  const id = `lane-${laneIds.size}`;
  laneIds.set(row, id);
  return id;
}
